import Axios from 'axios';
import { addRecipientViewMutation, downloadDocumentMutation } from '../graphql/document.mutations';
import { IDocument } from '@signing-page/documents/types/document';
import { IDocumentAttachment } from '@lib-shared/types/document-attachment';
import { apolloClient } from '@signing-page/apollo';
import { documentAttachmentsQuery } from '@signing-page/api/attachments/graphql/attachment.queries';
import {
  documentQuery,
  documentRevisionsQuery,
  editorRecipientInputQuery,
  getDownloadUrlQuery,
  recipientsQuery,
} from '@signing-page/api/documents/graphql/document.queries';
import { IRecipient } from '@signing-page/documents/types/recipient';
import { IInviteRecipientInfo } from '@signing-page/signing/types/invite-recipient-info';
import { EDocumentEvent } from '@signing-page/documents/types/enums/document-event';
import { EFetchPolicy } from '@lib-shared/api/types/enums/fetch-policy';
import { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';

export async function getDocument(): Promise<IDocument> {
  const {
    data: { document },
  } = await apolloClient.query({
    query: documentQuery,
    fetchPolicy: EFetchPolicy.NoCache,
  });
  return document;
}

export async function getDocumentRevisions(): Promise<Partial<IDocument>> {
  const {
    data: { document },
  } = await apolloClient.query({
    query: documentRevisionsQuery,
    fetchPolicy: EFetchPolicy.NoCache,
  });
  return document;
}

export async function loadEditorRecipientInput(): Promise<IEditorRecipientInput[]> {
  const {
    data: { editorRecipientInput },
  } = await apolloClient.query({
    query: editorRecipientInputQuery,
    fetchPolicy: EFetchPolicy.NetworkOnly,
  });
  return editorRecipientInput;
}

export async function addRecipientView(revision: number): Promise<boolean> {
  const {
    data: { addRecipientView },
  } = await apolloClient.mutate({
    mutation: addRecipientViewMutation,
    variables: { revision },
  });
  return addRecipientView;
}

export async function getDownloadUrl(): Promise<string> {
  const {
    data: { getDownloadUrl },
  } = await apolloClient.query({
    query: getDownloadUrlQuery,
    fetchPolicy: EFetchPolicy.NoCache,
  });
  return getDownloadUrl;
}

export async function getRecipients(): Promise<IRecipient[]> {
  const {
    data: {
      document: { recipients },
    },
  } = await apolloClient.query({
    query: recipientsQuery,
    fetchPolicy: EFetchPolicy.NoCache,
  });
  return recipients;
}

export async function getAttachments(): Promise<IDocumentAttachment[]> {
  const {
    data: {
      document: { documentAttachments },
    },
  } = await apolloClient.query({
    query: documentAttachmentsQuery,
    fetchPolicy: EFetchPolicy.NoCache,
  });
  return documentAttachments;
}

export async function download(): Promise<void> {
  await apolloClient.mutate({
    mutation: downloadDocumentMutation,
  });
}

export async function pushStatusToPusher(
  documentId: string,
  recipientId: string,
  authId: string,
  status: EDocumentEvent
): Promise<void> {
  const body = new FormData();
  body.append('action', `document.${status}`);
  await Axios.post(`/view/${documentId}/${recipientId}/${authId}/push`, body);
}

export async function logEvent(
  documentId: string,
  recipientId: string,
  authId: string,
  event: any
): Promise<void> {
  const body = new FormData();
  body.append('event_name', event);
  body.append('event_meta_id', '');
  body.append('event_meta_data', '');
  await Axios.post(`/view/${documentId}/${recipientId}/${authId}/event`, body);
}

export async function reject(
  reason: string,
  documentId: string,
  recipientId: string,
  authId: string
): Promise<void> {
  const body = new FormData();
  body.append('reason', reason);
  await Axios.post(`/view/${documentId}/${recipientId}/${authId}/reject`, body);
}

export async function forward(
  documentId: string,
  recipientId: string,
  authId: string,
  recipientInfo: IInviteRecipientInfo
): Promise<{ status: number; error: string }> {
  const body = new FormData();
  body.append('first-name', recipientInfo.firstName);
  body.append('last-name', recipientInfo.lastName);
  body.append('email', recipientInfo.email);
  body.append('mobile', recipientInfo.mobile);
  body.append('transfer', recipientInfo.transferSignature.toString());
  const { data } = await Axios.post(`/view/${documentId}/${recipientId}/${authId}/forward`, body);
  return data;
}

export default {
  getDocument,
  getDocumentRevisions,
  loadEditorRecipientInput,
  addRecipientView,
  getRecipients,
  getAttachments,
  getDownloadUrl,
  pushStatusToPusher,
  logEvent,
  reject,
  forward,
  download,
};
